import { useEffect, useState } from "react";
import {
  Box,
  styled,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";

import { DatePicker } from "@mui/x-date-pickers";
import { compareAsc, isSameDay, sub } from "date-fns";
import { MoonIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { useListSessionsSessionsGetQuery } from "api";
import { useFormat } from "hooks/useFormat";
import { toIsoString } from "utils/date.util";

const Wrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  paddingInline: theme.spacing(1.5),
  paddingBottom: theme.spacing(3),
  gap: theme.spacing(1),
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
  },
}));

type SessionSelectorProps = {
  subjectId: string;
  nightSessionId?: string | null;
};

export function SessionSelector({
  subjectId,
  nightSessionId,
}: SessionSelectorProps) {
  const { t } = useTranslation();

  const [searchParams, setSearchParams] = useSearchParams();
  const { format } = useFormat();

  const [now] = useState<Date>(new Date());

  const sessionId = searchParams.get("sessionId");

  const { data: sessionData, isLoading } = useListSessionsSessionsGetQuery({
    type: "vitalthings-somnofy-sm100-session",
    subjectId: subjectId,
    from: toIsoString(sub(now, { years: 1 })),
    sort: "desc",
    limit: 200,
  });

  const sessions = [...(sessionData?.data ?? [])].sort((a, b) =>
    compareAsc(a.session_end, b.session_end),
  );

  const session =
    sessions.find((session) => session.id === sessionId) ?? sessions.at(-1);

  const sameDaySessions = sessions.filter((s) =>
    isSameDay(s.session_end, session?.session_end ?? new Date()),
  );

  useEffect(() => {
    if (!sessionId && session) {
      setSearchParams(new URLSearchParams([["sessionId", session.id]]), {
        replace: true,
      });
    }
  }, [session, sessionId, setSearchParams]);

  return (
    <Wrapper>
      <DatePicker
        disableFuture
        onChange={(d) => {
          if (d) {
            const s = sessions
              .filter((s) => isSameDay(s.session_end, d))
              .sort((a, b) => b.epoch_count - a.epoch_count)
              .at(0);

            if (s) {
              setSearchParams(new URLSearchParams([["sessionId", s.id]]));
            }
          }
        }}
        value={
          session?.session_end ? new Date(session.session_end) : new Date()
        }
        shouldDisableDate={(d) =>
          !sessions.some((s) => isSameDay(new Date(s.session_end), d))
        }
      />
      {!isLoading && sessions.length === 0 && (
        <Box sx={{ display: "flex", alignItems: "center", padding: 1 }}>
          <Typography>{t("No sessions for subject yet")}</Typography>
        </Box>
      )}
      <ToggleButtonGroup
        value={session?.id}
        exclusive
        onChange={(_, sessionId) => {
          if (sessionId) {
            setSearchParams(new URLSearchParams([["sessionId", sessionId]]));
          }
        }}
      >
        {sameDaySessions.map((s) => (
          <ToggleButton key={s.id} value={s.id}>
            {format(s.session_start, "p")}
            {" - "}
            {format(s.session_end, "p")}
            {s.id === nightSessionId ? (
              <MoonIcon
                size={17}
                style={{ marginLeft: "4px", color: "inherit" }}
              />
            ) : null}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Wrapper>
  );
}
